import React, {
    useCallback, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Translator from '%app/js/common/services/translator';
import LabeledField from './LabeledField';
import RangeCalendar from './RangeCalendar';
import FIELD_SURFACE_SX from './fieldSurfaceSx';

const ISO_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'MMM D, YYYY';
const POPOVER_MIN_WIDTH = 320;

const TRIGGER_SX = {
    flex: 1,
    minWidth: 0,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'text.primary',
    fontFamily: 'inherit',
    textAlign: 'left',
    fontSize: { xs: '1rem', sm: '0.875rem' },
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const POPOVER_SX = {
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
};

const CHIPS_GRID_SX = {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'end',
    gap: 1,
};

const CHIP_LABEL_SX = {
    color: 'grey.500',
    mb: 0.5,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const CHIP_BOX_SX = {
    ...FIELD_SURFACE_SX,
    display: 'flex',
    alignItems: 'center',
    minHeight: 38,
    px: 1.25,
    fontSize: '0.875rem',
};

const CHIP_VALUE_SX = isPlaceholder => ({
    color: isPlaceholder ? 'grey.400' : 'text.primary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const DASH_SX = {
    pb: 1,
    color: 'grey.400',
    alignSelf: 'end',
};

const POPOVER_ACTIONS_SX = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
    mt: 0.5,
};

function formatTriggerDisplay(dateFrom, dateTo, placeholder) {
    if (!dateFrom && !dateTo) {
        return { text: placeholder, isPlaceholder: true };
    }
    const fromMoment = dateFrom ? moment(dateFrom, ISO_FORMAT) : null;
    const toMoment = dateTo ? moment(dateTo, ISO_FORMAT) : null;
    if (!fromMoment || !toMoment || fromMoment.isSame(toMoment, 'day')) {
        return { text: (fromMoment || toMoment).format(DISPLAY_FORMAT), isPlaceholder: false };
    }
    if (!fromMoment.isSame(toMoment, 'year')) {
        return {
            text: `${fromMoment.format(DISPLAY_FORMAT)} – ${toMoment.format(DISPLAY_FORMAT)}`,
            isPlaceholder: false,
        };
    }
    if (fromMoment.isSame(toMoment, 'month')) {
        return {
            text: `${fromMoment.format('MMM D')}–${toMoment.format('D, YYYY')}`,
            isPlaceholder: false,
        };
    }
    return {
        text: `${fromMoment.format('MMM D')} – ${toMoment.format(DISPLAY_FORMAT)}`,
        isPlaceholder: false,
    };
}

function formatChip(iso, placeholder) {
    if (!iso) {
        return { text: placeholder, isPlaceholder: true };
    }
    return { text: moment(iso, ISO_FORMAT).format(DISPLAY_FORMAT), isPlaceholder: false };
}

function WhenField({
    label, icon = null, placeholder, dateFrom, dateTo, onChange,
}) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState(dateFrom);
    const [draftTo, setDraftTo] = useState(dateTo);
    const [popoverWidth, setPopoverWidth] = useState(POPOVER_MIN_WIDTH);

    const fromLabel = Translator.trans('search_panel.when.from');
    const toLabel = Translator.trans('search_panel.when.to');
    const applyLabel = Translator.trans('search_panel.when.apply');
    const clearLabel = Translator.trans('search_panel.when.clear');
    const prevMonthLabel = Translator.trans('search_panel.when.prev_month');
    const nextMonthLabel = Translator.trans('search_panel.when.next_month');

    const triggerDisplay = formatTriggerDisplay(dateFrom, dateTo, placeholder);
    const fromChip = formatChip(draftFrom, fromLabel);
    const toChip = formatChip(draftTo, toLabel);

    const handleOpen = useCallback(() => {
        setDraftFrom(dateFrom);
        setDraftTo(dateTo);
        setOpen(true);
    }, [dateFrom, dateTo]);

    const handleClose = useCallback(() => setOpen(false), []);

    const handleCalendarChange = useCallback(({ dateFrom: nextFrom, dateTo: nextTo }) => {
        setDraftFrom(nextFrom);
        setDraftTo(nextTo);
    }, []);

    const handleApply = useCallback(() => {
        onChange({ dateFrom: draftFrom || '', dateTo: draftTo || '' });
        setOpen(false);
    }, [draftFrom, draftTo, onChange]);

    const handleClear = useCallback(() => {
        setDraftFrom('');
        setDraftTo('');
        onChange({ dateFrom: '', dateTo: '' });
        setOpen(false);
    }, [onChange]);

    useLayoutEffect(() => {
        if (open && anchorRef.current) {
            const measured = anchorRef.current.offsetWidth;
            setPopoverWidth(Math.max(measured, POPOVER_MIN_WIDTH));
        }
    }, [open]);

    return (
        <Box ref={anchorRef} sx={{ width: '100%', minWidth: 0 }}>
            <LabeledField
                label={label}
                icon={icon}
                active={open}
                renderInput={({ id }) => (
                    <Box
                        id={id}
                        component="button"
                        type="button"
                        onClick={handleOpen}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        aria-label={label}
                        data-testid="search-panel-when"
                        sx={{
                            ...TRIGGER_SX,
                            color: triggerDisplay.isPlaceholder ? 'grey.400' : 'text.primary',
                        }}
                    >
                        {triggerDisplay.text}
                    </Box>
                )}
            />
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { mt: 1, borderRadius: 2, width: popoverWidth } }}
            >
                <Box sx={POPOVER_SX} role="dialog" aria-label={label}>
                    <Box sx={CHIPS_GRID_SX}>
                        <Box>
                            <Typography component="div" variant="labelS" sx={CHIP_LABEL_SX}>
                                {fromLabel}
                            </Typography>
                            <Box sx={CHIP_BOX_SX} data-testid="search-panel-when-from">
                                <Box component="span" sx={CHIP_VALUE_SX(fromChip.isPlaceholder)}>
                                    {fromChip.text}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={DASH_SX} aria-hidden="true">—</Box>
                        <Box>
                            <Typography component="div" variant="labelS" sx={CHIP_LABEL_SX}>
                                {toLabel}
                            </Typography>
                            <Box sx={CHIP_BOX_SX} data-testid="search-panel-when-to">
                                <Box component="span" sx={CHIP_VALUE_SX(toChip.isPlaceholder)}>
                                    {toChip.text}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <RangeCalendar
                        dateFrom={draftFrom}
                        dateTo={draftTo}
                        onChange={handleCalendarChange}
                        prevLabel={prevMonthLabel}
                        nextLabel={nextMonthLabel}
                    />
                    <Box sx={POPOVER_ACTIONS_SX}>
                        <Button
                            type="button"
                            onClick={handleClear}
                            data-testid="search-panel-when-clear"
                            sx={{ textTransform: 'none' }}
                        >
                            {clearLabel}
                        </Button>
                        <Button
                            type="button"
                            variant="contained"
                            onClick={handleApply}
                            data-testid="search-panel-when-apply"
                            sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                            {applyLabel}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
}

WhenField.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.node,
    placeholder: PropTypes.string.isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default WhenField;
